import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionStore, PaymentCard } from 'src/store/subscriptionStore';
import {
  usePaymentMethodsQuery,
  useDeletePaymentMethodMutation,
} from 'src/api/subscription/useSubscriptionApi';
import { ReactComponent as UserChevronLeftSVG } from 'src/assets/images/onboarding-trainer/components/sidebar/user-chevron-left.svg';
import { ReactComponent as AddButtonSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/checkout/add-button.svg';
import { ReactComponent as RemoveCardSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/manage-payment-method/remove-card.svg';
import { ReactComponent as ExpiresSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/manage-payment-method/expires.svg';
import { ReactComponent as CardholderNameSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/manage-payment-method/cardholder-name.svg';
import { CardBrandLogo } from '../components/CardBrandLogo';
import { formatMaskedCardNumber, getCardBrandDisplayName } from 'src/utils/cardUtils';
import { PaymentMethodModal } from '../modals/PaymentMethodModal';
import { RemovePaymentMethodModal } from '../modals/RemovePaymentMethodModal';
import { toast } from 'src/utils/appAlert';
import './ManagePaymentMethod.scss';

export const ManagePaymentMethod: FC = () => {
  const navigate = useNavigate();
  const { paymentMethods, removePaymentMethod } = useSubscriptionStore();
  const { data: serverMethods, refetch: refetchMethods } = usePaymentMethodsQuery();
  const deleteCardMutation = useDeletePaymentMethodMutation();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<PaymentCard | null>(null);
  const [cardToRemove, setCardToRemove] = useState<PaymentCard | null>(null);

  const defaultId = serverMethods?.default_payment_method_id;
  const rawCards = serverMethods?.payment_methods || paymentMethods;

  let singleDefaultId = defaultId;
  if (!singleDefaultId) {
    const foundDefault = rawCards.find((c: any) => c.is_default || c.isDefault);
    singleDefaultId = foundDefault ? foundDefault.id : rawCards[0]?.id;
  }

  const cards: PaymentCard[] = rawCards.map((c: any) => ({
    id: c.id,
    brand: (c.brand?.toLowerCase() || 'visa') as any,
    last4: c.last4,
    expiry:
      c.expiry ||
      (c.exp_month && c.exp_year
        ? `${String(c.exp_month).padStart(2, '0')}/${String(c.exp_year).slice(-2)}`
        : '—'),
    cardholderName: c.cardholder_name || c.cardholderName || '—',
    isDefault: Boolean(singleDefaultId && c.id === singleDefaultId),
  }));

  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/onboarding-trainer/dashboard/subscription/my-subscription');
    }
  };

  const handleEditCard = (card: PaymentCard) => {
    setEditingCard(card);
    setIsAddModalOpen(true);
  };

  const handleOpenRemoveDialog = (card: PaymentCard) => {
    setCardToRemove(card);
  };

  const handleConfirmRemove = () => {
    if (cardToRemove) {
      deleteCardMutation.mutate(cardToRemove.id, {
        onSuccess: () => {
          removePaymentMethod(cardToRemove.id);
          toast.success('Card removed successfully.');
          setCardToRemove(null);
        },
        onError: (err: any) => {
          toast.error(err.userMessage || 'Failed to remove card.');
          setCardToRemove(null);
        },
      });
    }
  };

  return (
    <div className="manage-payment-page-container">
      {/* Header */}
      <div className="manage-payment-header">
        <div className="header-titles">
          <button type="button" className="manage-back-btn" onClick={handleBack}>
            <UserChevronLeftSVG />
            <span>{'Manage Payment Method'}</span>
          </button>
          <p className="manage-subtitle">
            {'Update or add a payment method for your subscription.'}
          </p>
        </div>
      </div>

      {/* Main Container */}
      <div className={`payment-methods-box ${cards.length <= 1 ? 'single-box' : 'multi-box'}`}>
        <div className="payment-box-header">
          <h3>{'Current Payment Method'}</h3>
          <button
            type="button"
            className="btn-add-card-white"
            onClick={() => {
              setEditingCard(null);
              setIsAddModalOpen(true);
            }}
          >
            <AddButtonSVG />
            <span>{'Add'}</span>
          </button>
        </div>

        <div className={`cards-grid ${cards.length > 1 ? 'multi-cards' : 'single-card'}`}>
          {cards.map((card) => (
            <div key={card.id} className="payment-card-box">
              <div className="card-top-header">
                <div className="brand-and-mask">
                  <div className="brand-icon">
                    <CardBrandLogo brand={card.brand} />
                  </div>
                  <div className="title-and-mask">
                    <span className="card-brand-name">{getCardBrandDisplayName(card.brand)}</span>
                    <span className="card-full-mask">
                      {formatMaskedCardNumber(card.brand, card.last4)}
                    </span>
                  </div>
                </div>

                {card.isDefault && <span className="card-default-pill">{'Default'}</span>}
              </div>

              <div className="card-meta-details">
                <div className="meta-item">
                  <div className="meta-icon-label">
                    <ExpiresSVG />
                    <span>{'Expires'}</span>
                  </div>
                  <span className="meta-val">{card.expiry || '—'}</span>
                </div>

                <div className="meta-item">
                  <div className="meta-icon-label">
                    <CardholderNameSVG />
                    <span>{'Cardholder Name'}</span>
                  </div>
                  <span className="meta-val" title={card.cardholderName || ''}>
                    {card.cardholderName || '—'}
                  </span>
                </div>
              </div>

              {/* Actions */}
              {cards.length === 1 ? (
                <div className="card-action-single">
                  <button
                    type="button"
                    className="btn-edit-card"
                    onClick={() => handleEditCard(card)}
                  >
                    {'Edit Card'}
                  </button>
                </div>
              ) : (
                <div className="card-action-split">
                  <button
                    type="button"
                    className="btn-update-card"
                    onClick={() => handleEditCard(card)}
                  >
                    {'Update'}
                  </button>
                  <button
                    type="button"
                    className="btn-remove-card"
                    onClick={() => handleOpenRemoveDialog(card)}
                  >
                    <RemoveCardSVG />
                    <span>{'Remove'}</span>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="encryption-footer-note">
          <p>{'Your payment information is secure and encrypted.'}</p>
        </div>
      </div>

      {/* Modals */}
      <PaymentMethodModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingCard(null);
        }}
        cardToEdit={editingCard}
        onSuccess={() => {
          void refetchMethods();
        }}
      />

      <RemovePaymentMethodModal
        isOpen={Boolean(cardToRemove)}
        onClose={() => setCardToRemove(null)}
        onConfirmRemove={handleConfirmRemove}
      />
    </div>
  );
};
